import { SkyBadge, SkyButton, SkyDrawer, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { CopyIcon } from "@/lib/icons";
import { formatInstant, instantHoverTitle, isIsoInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import {
  auditActionLabelFor,
  auditReadKind,
  isReadEvent,
  type AuditReadKind,
} from "./actionLabels";
import { labelMetadataOf, type AuditGridRow } from "./AuditEventsGrid";
import { byCodeUnit } from "@shared/sort";
import { ROLE_LABELS, type RoleName } from "@shared/permissions";
import { ACCESS_REVOKE_REASON_WORDS, isAccessRevokeReasonCode } from "@shared/audit/revokeReasons";
import { DESTINATION_DRAFT_REASONS } from "@shared/warehouse/destinationReadiness";
import type { DestinationReadinessView } from "@shared/types/instances";
import { chipOf, readinessReasonLine } from "@/hooks/use-destination-readiness";
import { FIELD_LABELS } from "@/components/settings/DestinationForm";

/**
 * What one event changed (canvas 09 block 03; SEC-17, SEC-18).
 *
 * Before and after are presented as a two-column definition list rather than a
 * JSON dump, so a screen reader reads "field, then value" instead of a wall of
 * braces, and a reviewer can scan the one line that changed.
 *
 * A secret shows THAT it changed and which vault reference it now points at,
 * never a value. Nothing is redacted here: the snapshots were redacted when the
 * row was written, so anything this component receives is already safe to render
 * — and a value that reached the table would not be made safe by hiding it from
 * one reader.
 */

/** The marker `server/audit/redact.ts` leaves behind where a secret used to be. */
const REDACTED = "***";

/**
 * A nested snapshot is shown as JSON, because a definition list of a definition
 * list is harder to read than the shape itself. Two things are done to it: the
 * redaction marker is spelled out, so a reader is told a secret was removed
 * rather than shown three asterisks and left to guess, and a very long value is
 * cut, because a thousand characters of entity array buries the one field that
 * changed.
 */
const MAX_JSON_LENGTH = 400;

/** An instant in the display zone, named; with `millis` the fraction too (see `ValuePair`). */
function instantText(iso: string, zone: string, millis: boolean): string {
  const text = formatInstant(iso, zone, "seconds", { named: true });
  if (!millis) return text;
  const ms = String(new Date(iso).getTime() % 1000).padStart(3, "0");
  return text.replace(/(\d{1,2}:\d{2}:\d{2})/, `$1.${ms}`);
}

/**
 * Nested instants have no hover of their own: the preview is one text node, so one `title` cannot
 * name each instant. The zone is named in every one instead.
 */
function jsonPreview(value: unknown, zone: string, millis = false): string {
  // #1950: an instant inside a nested snapshot reads in the display zone too, never as ISO UTC.
  const text = JSON.stringify(value, (_key, item: unknown) =>
    isIsoInstant(item) ? instantText(item, zone, millis) : item,
  ).replaceAll(`"${REDACTED}"`, '"(redacted)"');
  return text.length > MAX_JSON_LENGTH ? `${text.slice(0, MAX_JSON_LENGTH)}…` : text;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Whether two snapshot values are the same, decided on the raw values and never on the words
 * shown: an instant by its epoch milliseconds (the same moment in two offsets is the same), a
 * nested value by its JSON with every instant normalised.
 */
function sameValue(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  if (isIsoInstant(a) && isIsoInstant(b)) return new Date(a).getTime() === new Date(b).getTime();
  const normal = (value: unknown): string | undefined =>
    JSON.stringify(value, (_key, item: unknown) => {
      if (isIsoInstant(item)) return { epochMs: new Date(item).getTime() };
      if (isRecord(item)) {
        return Object.fromEntries(
          Object.keys(item)
            .sort(byCodeUnit)
            .map((k) => [k, item[k]]),
        );
      }
      return item;
    });
  return Object.is(a, b) || normal(a) === normal(b);
}

function display(
  value: unknown,
  zone: string,
  millis = false,
): { text: string; redacted: boolean; title?: string } {
  if (value === undefined) return { text: "None", redacted: false };
  if (value === null) return { text: "None", redacted: false };
  if (value === REDACTED) return { text: "(redacted)", redacted: true };
  if (isIsoInstant(value)) {
    // #1950 (owner rule on #1078): an instant is read in the person's display zone, named.
    return {
      text: instantText(value, zone, millis),
      redacted: false,
      title: instantHoverTitle(value, zone),
    };
  }
  if (typeof value === "string") return { text: value, redacted: false };
  if (typeof value === "number" || typeof value === "boolean") {
    return { text: String(value), redacted: false };
  }
  if (isRecord(value) && typeof value.vaultName === "string") {
    // A `secret_ref`, already reduced to its pointer on the way in.
    return { text: `${value.vaultName} / ${String(value.secretName ?? "")}`, redacted: false };
  }
  return { text: jsonPreview(value, zone, millis), redacted: false };
}

function namesAt(metadata: unknown, key: string): string[] {
  if (!isRecord(metadata)) return [];
  const value = metadata[key];
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function markedFieldName(name: string, cleared: Set<string>, replaced: Set<string>): string {
  if (cleared.has(name)) return `${name} (cleared)`;
  if (replaced.has(name)) return `${name} (replaced)`;
  return name;
}

/**
 * What a write actually moved, by name, when the row records it (`metadata.changedFields`). The
 * destination writes carry it since 2026-09-28, and the notification settings writes already
 * did (`NotificationService`, API-STANDARD), so both show the line. A credential is marked
 * replaced or cleared from the row's `rotated_fields` and `cleared_fields`; its value never
 * reaches the trail. Null when the row carries no such list, so older rows and actions that
 * record none show nothing new.
 */
function changedFieldsLine(metadata: unknown): string | null {
  if (!isRecord(metadata) || !Array.isArray(metadata.changedFields)) return null;
  const replaced = new Set(namesAt(metadata, "rotated_fields"));
  const cleared = new Set(namesAt(metadata, "cleared_fields"));
  const names = namesAt(metadata, "changedFields").map((name) =>
    markedFieldName(name, cleared, replaced),
  );
  return names.length > 0 ? names.join(", ") : "None";
}

function countAt(metadata: Record<string, unknown>, key: string): number {
  const value = metadata[key];
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : 0;
}

function tablesWord(count: number): string {
  return count === 1 ? "1 table" : `${count} tables`;
}

/**
 * #2492: a destination move saved with "Save and read everything again" records how many tables
 * had their load bounds cleared (`bounds_reset`) and their table name case reset
 * (`table_cases_reset`), counts only. Neither is in the binding snapshots, so the drawer says
 * them here. Null when the row records no reset.
 */
function resetLine(metadata: unknown): string | null {
  if (!isRecord(metadata)) return null;
  const bounds = countAt(metadata, "bounds_reset");
  const cases = countAt(metadata, "table_cases_reset");
  if (bounds === 0 && cases === 0) return null;
  const parts = [
    'Saved with "Save and read everything again".',
    bounds > 0
      ? `The load position was cleared on ${tablesWord(bounds)}, so each one reads all of its data on its next import.`
      : null,
    cases > 0 ? `The table name case was reset on ${tablesWord(cases)}.` : null,
  ].filter((part): part is string => part !== null);
  return parts.join(" ");
}

/** #2519: a role key ("InstanceAdmin") as the Users page names it ("Instance admin"). */
function isRoleName(value: unknown): value is RoleName {
  return typeof value === "string" && Object.hasOwn(ROLE_LABELS, value);
}

const ROLE_FIELDS: ReadonlySet<string> = new Set(["roleName", "role"]);

/** A snapshot value as the drawer shows it: a role field's key in the Users page's words. */
function shownValue(field: string, value: unknown): unknown {
  return ROLE_FIELDS.has(field) && isRoleName(value) ? ROLE_LABELS[value] : value;
}

/** #2519: the Reason line, a closed revoke code read in words, never the code. */
function reasonText(row: AuditGridRow): string | null {
  if (!row.reason) return null;
  if (row.action !== "access.revoke" || !isAccessRevokeReasonCode(row.reason)) return row.reason;
  const metadata = isRecord(row.event?.metadata) ? row.event.metadata : {};
  const role = metadata.replaced_by_role_name;
  if (row.reason === "replaced" && isRoleName(role)) {
    return `Replaced by a new role: ${ROLE_LABELS[role]}.`;
  }
  return ACCESS_REVOKE_REASON_WORDS[row.reason];
}

/**
 * #2518: a destination's snapshot carries two things that are not settings a person typed: its
 * readiness (derived, and moved by the trigger when a tested field changes) and the fields the
 * deployment fixes. Neither is listed among the settings; each reads as its own line in words.
 */
const BINDING_ENTITY = "instance_binding";
const BINDING_LINE_FIELDS: ReadonlySet<string> = new Set(["readiness", "fixedByDeployment"]);

/**
 * A snapshot's readiness as the Destination page reads one, or null when it is not a readiness.
 * Its last test is read apart (`lastTestWords`): a snapshot stored before #2518 has a null tester.
 */
function readinessOf(value: unknown): DestinationReadinessView | null {
  if (!isRecord(value)) return null;
  const { state, reason, missing, source } = value;
  if (state !== "not_set" && state !== "draft" && state !== "ready") return null;
  return {
    state,
    reason:
      typeof reason === "string" &&
      (DESTINATION_DRAFT_REASONS as readonly string[]).includes(reason)
        ? (reason as DestinationReadinessView["reason"])
        : null,
    missing: Array.isArray(missing)
      ? missing.filter((item): item is string => typeof item === "string")
      : [],
    source: source === "test" || source === "carried" ? source : null,
    lastTest: null,
  };
}

/**
 * The readiness's last test, "Last test passed <instant> by <name>", or null. The tester is named
 * only when the snapshot names one: a write's snapshot stored before #2518 holds a null there,
 * which is not "nobody tested it", so it is left out rather than read.
 */
function lastTestWords(value: unknown, zone: string): string | null {
  if (!isRecord(value) || !isRecord(value.lastTest)) return null;
  const { at, by, outcome } = value.lastTest;
  if (!isIsoInstant(at) || (outcome !== "passed" && outcome !== "failed")) return null;
  const named = typeof by === "string" && by.trim() !== "" ? ` by ${by}` : "";
  return `Last test ${outcome} ${instantText(at, zone, false)}${named}.`;
}

interface DestinationStateLine {
  /** The state before the change, when it differs from `to`. */
  from: string | null;
  /** The Destination page's chip words (`chipOf`), so the two never name one state apart. */
  to: string;
  /** Why it is Draft, in the banner's words (`readinessReasonLine`). */
  reason: string | null;
  /** Why it was Draft before the change, when that differs from `reason`, so no move is lost. */
  wasReason: string | null;
  lastTest: string | null;
}

function destinationStateLine(
  event: AuditGridRow["event"] | undefined,
  read: Record<string, unknown> | null,
  zone: string,
): DestinationStateLine | null {
  if (read) {
    const readiness = readinessOf(read.readiness);
    if (!readiness) return null;
    return {
      from: null,
      to: chipOf(readiness).label,
      reason: readinessReasonLine(readiness),
      wasReason: null,
      lastTest: lastTestWords(read.readiness, zone),
    };
  }
  const before = isRecord(event?.before) ? event.before.readiness : undefined;
  const after = isRecord(event?.after) ? event.after.readiness : undefined;
  const was = readinessOf(before);
  const now = readinessOf(after);
  if (!was && !now) return null;
  const from = was ? chipOf(was).label : null;
  const to = now ? chipOf(now).label : "Not recorded";
  const reason = now ? readinessReasonLine(now) : null;
  const wasReason = was ? readinessReasonLine(was) : null;
  return {
    from: from && from !== to ? from : null,
    to,
    reason,
    // A move between two Draft reasons keeps one label ("Draft"), so the earlier reason says it.
    wasReason: wasReason && wasReason !== reason ? wasReason : null,
    lastTest: lastTestWords(after, zone) ?? lastTestWords(before, zone),
  };
}

/** #2501's fixed fields by the Destination page's names. */
const FIXED_FIELD_NAMES: Readonly<Record<string, string>> = {
  tableNameCase: "Table name case",
  metadataColumns: "skyData columns",
};

function fixedNames(value: unknown): string {
  const names = Array.isArray(value)
    ? value
        .filter((item): item is string => typeof item === "string")
        .map((field) => FIXED_FIELD_NAMES[field] ?? field)
    : [];
  return names.length > 0 ? names.join(", ") : "None";
}

/** #2518: the fields the deployment fixes, in words, with an arrow only when the list moved. */
function fixedFieldsLine(
  event: AuditGridRow["event"] | undefined,
  read: Record<string, unknown> | null,
): string | null {
  const key = "fixedByDeployment";
  if (read) return Object.hasOwn(read, key) ? fixedNames(read[key]) : null;
  const before = isRecord(event?.before) && Object.hasOwn(event.before, key);
  const after = isRecord(event?.after) && Object.hasOwn(event.after, key);
  if (!before && !after) return null;
  const from = fixedNames(before ? (event?.before as Record<string, unknown>)[key] : undefined);
  const to = fixedNames(after ? (event?.after as Record<string, unknown>)[key] : undefined);
  return from === to ? to : `${from} → ${to}`;
}

/** Every field either snapshot mentions, so a removal is as visible as an addition. */
function changedFields(before: unknown, after: unknown): string[] {
  const keys = new Set<string>();
  if (isRecord(before)) for (const key of Object.keys(before)) keys.add(key);
  if (isRecord(after)) for (const key of Object.keys(after)) keys.add(key);
  return [...keys].sort(byCodeUnit);
}

/** The fields the drawer lists: a read's record's, else every field either snapshot mentions. */
function snapshotFields(
  event: AuditGridRow["event"] | undefined,
  read: Record<string, unknown> | null,
): string[] {
  if (read) return Object.keys(read).sort(byCodeUnit);
  return event ? changedFields(event.before, event.after) : [];
}

/**
 * #1376: what a read event read. A read stores the record it showed as its `after` (or, in an
 * older row, its `before`); it changed nothing, so the drawer lists that record's fields with
 * their values, never a before and after.
 */
function readSnapshot(event: AuditGridRow["event"] | undefined): Record<string, unknown> | null {
  if (!event) return null;
  if (isRecord(event.after)) return event.after;
  return isRecord(event.before) ? event.before : null;
}

/**
 * #1248: a refused field's name as the form shows it. The destination's own labels, the secret
 * fields' words, else the stored name (a `secrets.` prefix dropped).
 */
const REFUSED_FIELD_LABELS: Readonly<Record<string, string>> = {
  ...FIELD_LABELS,
  snowflakePrivateKey: "Private key",
  snowflakeKeyPassphrase: "Key passphrase",
  databricksSpSecret: "Service principal secret",
  dbtGitSecret: "dbt repository token",
  teamsWebhook: "Teams webhook",
};

function refusedFieldsLine(metadata: unknown): string | null {
  const names = namesAt(metadata, "refused_fields").map((name) => {
    const field = name.startsWith("secrets.") ? name.slice("secrets.".length) : name;
    return Object.hasOwn(REFUSED_FIELD_LABELS, field) ? REFUSED_FIELD_LABELS[field]! : field;
  });
  return names.length > 0 ? names.join(", ") : null;
}

/** #1248: a refusal code in words, with the code kept for the reader who searches by it. */
const FAILURE_REASON_WORDS: Readonly<Record<string, string>> = {
  VALIDATION_FAILED: "Some fields were empty or not valid",
};

function failureReasonText(code: string | null): string | null {
  if (code === null) return null;
  return Object.hasOwn(FAILURE_REASON_WORDS, code)
    ? `${FAILURE_REASON_WORDS[code]} (${code})`
    : code;
}

function failureReasonOf(event: AuditGridRow["event"] | undefined): string | null {
  if (!event) return null;
  if (typeof event.failureReason === "string") return event.failureReason;
  return isRecord(event.metadata) && typeof event.metadata.reason === "string"
    ? event.metadata.reason
    : null;
}

function Row({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="py-1 pr-4 align-top">
        <SkyText size={200} color="muted">
          {term}
        </SkyText>
      </dt>
      <dd className="py-1">{children}</dd>
    </>
  );
}

/** One value a read showed, as it was read (#1376). */
function ReadValue({ value, zone }: { value: unknown; zone: string }) {
  const shown = display(value, zone);
  return (
    <span title={shown.title}>
      <SkyText size={200} color={shown.redacted ? "muted" : "default"}>
        {shown.text}
      </SkyText>
    </span>
  );
}

function ValuePair({ before, after, zone }: { before: unknown; after: unknown; zone: string }) {
  const same = sameValue(before, after);
  let from = display(before, zone);
  let to = display(after, zone);
  if (!same && from.text === to.text) {
    // Different values that read alike to the second: show the fraction so the change is seen.
    from = display(before, zone, true);
    to = display(after, zone, true);
  }
  if (same) {
    return (
      <span title={to.title}>
        <SkyText size={200} color={to.redacted ? "muted" : "default"}>
          {to.text}
        </SkyText>
      </span>
    );
  }
  if (from.text === to.text) {
    // Different values that still read alike: keep the pair and say why, never one value.
    let marker = "(changed beyond preview)";
    if (isRecord(before) && isRecord(after) && typeof after.vaultName === "string") {
      marker = "(new version)";
    } else if (typeof before !== typeof after) {
      marker = "(type changed)";
    }
    to = { ...to, text: `${to.text} ${marker}` };
  }
  return (
    <SkyText size={200}>
      <span className="line-through opacity-70" title={from.title}>
        {from.text}
      </span>
      {" → "}
      <span className="font-medium" title={to.title}>
        {to.text}
      </span>
    </SkyText>
  );
}

/** #2518: the destination's state in the Destination page's words, its reasons and last test. */
function DestinationStateRow({ line }: { line: DestinationStateLine }) {
  const notes = [line.reason, line.wasReason ? `Was: ${line.wasReason}` : null, line.lastTest];
  return (
    <Row term="Destination state">
      <span className="flex flex-col" data-testid="audit-destination-state">
        <SkyText size={200}>
          {line.from ? (
            <>
              <span className="line-through opacity-70">{line.from}</span>
              {" → "}
              <span className="font-medium">{line.to}</span>
            </>
          ) : (
            line.to
          )}
        </SkyText>
        {notes
          .filter((note): note is string => note !== null)
          .map((note) => (
            <SkyText key={note} size={200} color="muted">
              {note}
            </SkyText>
          ))}
      </span>
    </Row>
  );
}

/** The event's facts and its field-by-field change, with the reference to copy. */
function AuditDiff({
  row,
  event,
  fields,
  changedLine,
  resetNote,
  read,
  destinationState,
  fixedFields,
  zone,
  onCopyReference,
}: {
  row: AuditGridRow;
  event: AuditGridRow["event"] | undefined;
  fields: string[];
  changedLine: string | null;
  /** #2492: what "Save and read everything again" cleared, from the row's counts. */
  resetNote: string | null;
  /** #1376: a read's record, listed field by field; null for every other event. */
  read: Record<string, unknown> | null;
  /** #2518: a destination's state in words, on its own line; null for any other entity. */
  destinationState: DestinationStateLine | null;
  /** #2518: the fields the deployment fixes, in words; null when the snapshot names none. */
  fixedFields: string | null;
  zone: string;
  onCopyReference: (reference: string) => void;
}) {
  const reason = reasonText(row);
  return (
    <dl className="grid grid-cols-[minmax(9rem,auto)_1fr] gap-x-4" data-testid="audit-diff">
      <Row term="Entity">
        <SkyText size={200}>
          {row.entityType}
          {row.entityId ? ` ${row.entityId}` : ""}
        </SkyText>
      </Row>
      {row.entityLabel ? (
        <Row term="Label">
          <SkyText size={200}>{row.entityLabel}</SkyText>
        </Row>
      ) : null}
      {reason ? (
        <Row term="Reason">
          <span data-testid="audit-reason">
            <SkyText size={200}>{reason}</SkyText>
          </span>
        </Row>
      ) : null}
      {changedLine ? (
        <Row term="Changed fields">
          <span data-testid="audit-changed-fields">
            <SkyText size={200}>{changedLine}</SkyText>
          </span>
        </Row>
      ) : null}
      {resetNote ? (
        <Row term="Reset">
          <span data-testid="audit-reset-note">
            <SkyText size={200}>{resetNote}</SkyText>
          </span>
        </Row>
      ) : null}

      {fields.map((field) => (
        <Row key={field} term={field}>
          {read ? (
            <ReadValue value={shownValue(field, read[field])} zone={zone} />
          ) : (
            <ValuePair
              zone={zone}
              before={shownValue(field, isRecord(event?.before) ? event?.before[field] : undefined)}
              after={shownValue(field, isRecord(event?.after) ? event?.after[field] : undefined)}
            />
          )}
        </Row>
      ))}

      {destinationState ? <DestinationStateRow line={destinationState} /> : null}
      {fixedFields ? (
        <Row term="Set by Skypoint">
          <span data-testid="audit-fixed-by-deployment">
            <SkyText size={200}>{fixedFields}</SkyText>
          </span>
        </Row>
      ) : null}

      <Row term="Reference">
        <span className="flex items-center gap-2">
          <SkyText size={200} font="numeric">
            {row.correlationId}
          </SkyText>
          <SkyButton
            appearance="subtle"
            size="small"
            icon={CopyIcon}
            testId="copy-reference"
            aria-label="Copy the reference id"
            onClick={() => onCopyReference(row.correlationId)}
          >
            Copy
          </SkyButton>
        </span>
      </Row>
      {event?.chainSeq ? (
        <Row term="Chain position">
          <SkyText size={200} font="numeric">
            {event.chainSeq}
          </SkyText>
        </Row>
      ) : null}
      <Row term="Source">
        <SkyText size={200}>
          {event?.source ?? "api"} · {row.actorType}
        </SkyText>
      </Row>
    </dl>
  );
}

/** The words of a read's bar, by what kind of read it was (#1376, #1373 review). */
function readBarWords(
  kind: AuditReadKind,
  listsFields: boolean,
  event: AuditGridRow["event"] | undefined,
): { intent: "info" | "warning"; title: string; message: string } {
  if (kind === "secret") {
    return {
      intent: "info",
      title: "Stored secret used",
      message: listsFields
        ? "A stored secret was used for this person's request. Its value is never recorded. The fields below name where it is stored."
        : "A stored secret was used for this person's request. Its value is never recorded.",
    };
  }
  if (kind === "connected_app") {
    const metadata = isRecord(event?.metadata) ? event.metadata : {};
    if (metadata.outcome === "failed") {
      const reason = typeof metadata.reason === "string" ? metadata.reason : null;
      return {
        intent: "warning",
        title: "This tool call was refused",
        message: [
          "A connected app called a read tool for this person and was refused. Nothing was read.",
          reason ? `Reason code: ${reason}.` : null,
        ]
          .filter((part): part is string => part !== null)
          .join(" "),
      };
    }
    return {
      intent: "info",
      title: "Read through a connected app",
      message: "A connected app called a read tool for this person. Nothing was saved.",
    };
  }
  if (kind === "record") {
    return {
      intent: "info",
      title: "Read, nothing changed",
      message: listsFields
        ? "This person opened the record and read it. The fields below are what was read, as it stood then. Nothing was saved."
        : "This person opened the record and read it. Nothing was saved.",
    };
  }
  return {
    intent: "info",
    title: "Read, nothing changed",
    message: "This person read it. Nothing was saved.",
  };
}

function ReadBar({
  kind,
  listsFields,
  event,
}: {
  kind: AuditReadKind;
  listsFields: boolean;
  event: AuditGridRow["event"] | undefined;
}) {
  const words = readBarWords(kind, listsFields, event);
  return (
    <SkyMessageBar
      intent={words.intent}
      layout="multiline"
      testId="drawer-read"
      title={words.title}
      message={words.message}
    />
  );
}

/** The refused attempt's bar: nothing changed, why, and (#1248) which fields were refused. */
function failedAttemptMessage(event: AuditGridRow["event"] | undefined): string {
  const reason = failureReasonText(failureReasonOf(event));
  const refused = refusedFieldsLine(event?.metadata);
  return [
    "Nothing was changed.",
    reason ? `Reason: ${reason}.` : null,
    refused ? `Fields refused: ${refused}.` : null,
    "The reference ties it to the error the person saw.",
  ]
    .filter((part): part is string => part !== null)
    .join(" ");
}

export function AuditEventDrawer({
  row,
  onOpenChange,
  onCopyReference,
}: {
  row: AuditGridRow | null;
  onOpenChange: (open: boolean) => void;
  onCopyReference: (reference: string) => void;
}) {
  const zone = useDisplayTz();
  const event = row?.event;
  // #1376: a read lists the record it read; only a change is laid out as before and after. A
  // secret read lists where the secret is stored (its value is never recorded). A connected
  // app's read and any other read store no record.
  const readKind = row && !row.failed ? auditReadKind(row.action) : null;
  const read = readKind === "record" || readKind === "secret" ? readSnapshot(event) : null;
  const binding = row?.entityType === BINDING_ENTITY;
  const fields = snapshotFields(event, read).filter(
    (field) => !(binding && BINDING_LINE_FIELDS.has(field)),
  );
  const destinationState = binding ? destinationStateLine(event, read, zone) : null;
  const fixedFields = binding ? fixedFieldsLine(event, read) : null;
  const changedLine = event ? changedFieldsLine(event.metadata) : null;
  const resetNote = event ? resetLine(event.metadata) : null;

  return (
    <SkyDrawer
      open={row !== null}
      onOpenChange={onOpenChange}
      position="end"
      size="large"
      testId="audit-drawer"
      headerTitle={
        row
          ? (auditActionLabelFor(row.action, row.failed === true, labelMetadataOf(row)) ??
            row.action)
          : "Change"
      }
      subtitle={
        row
          ? `${formatInstant(row.occurredAt, zone, "seconds", { named: true })} · ${row.actorEmail ?? row.actorRef} · ${row.scopeType} scope`
          : undefined
      }
    >
      {row ? (
        <div className="space-y-4">
          {row.breakGlass ? (
            <SkyMessageBar
              intent="error"
              layout="multiline"
              testId="drawer-break-glass"
              title="Break-glass grant"
              message="The grantor and the grantee are the same person, so this grant required a reason and was recorded with metadata.break_glass = true."
            />
          ) : null}

          {readKind ? <ReadBar kind={readKind} listsFields={read !== null} event={event} /> : null}

          <AuditDiff
            row={row}
            event={event}
            fields={fields}
            changedLine={changedLine}
            resetNote={resetNote}
            read={read}
            destinationState={destinationState}
            fixedFields={fixedFields}
            zone={zone}
            onCopyReference={onCopyReference}
          />

          {row.failed ? (
            <SkyMessageBar
              intent="warning"
              layout="multiline"
              testId="drawer-failed-attempt"
              title="This attempt was refused"
              message={failedAttemptMessage(event)}
            />
          ) : event &&
            !isReadEvent(row.action) &&
            !isRecord(event.before) &&
            !isRecord(event.after) ? (
            <SkyMessageBar
              intent="info"
              layout="multiline"
              title="This event records no field change"
              message="A read, an export or a sign-in has no before and after. What it did is the action itself, and the reference ties it to the request."
            />
          ) : null}

          <SkyBadge appearance="tint" color="subtle">
            Secret values never enter the audit trail
          </SkyBadge>
        </div>
      ) : (
        <span />
      )}
    </SkyDrawer>
  );
}
